import type { ChatbotTurn } from '@/types/chatbot.model'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { ChatbotSessionId } from '@/utils/constants/session-storage'

interface ChatbotState {
  chatTurns: ChatbotTurn[]
  isSidebarOpen: boolean
  isStreaming: boolean
  selectedSessionId: string
  streamError: string
  streamingStatus: string
}

/**
 * The selected session is persisted so that a page reload - or opening the
 * sidebar on a freshly loaded page - resumes the conversation the user was
 * having. Only the id is stored; the turns are re-fetched from the session
 * history endpoint.
 *
 * Reads and writes are guarded because this runs while the store is being
 * constructed, and session storage is unavailable in some embedded contexts.
 */
function readSelectedSessionId(): string {
  try {
    return sessionStorage.getItem(ChatbotSessionId) || ''
  }
  catch {
    return ''
  }
}

function persistSelectedSessionId(sessionId: string) {
  try {
    if (sessionId) {
      sessionStorage.setItem(ChatbotSessionId, sessionId)
    }
    else {
      sessionStorage.removeItem(ChatbotSessionId)
    }
  }
  catch {
    // Persistence is a convenience - the conversation still works in memory.
  }
}

export const useChatbotStore = defineStore('chatbot', {
  state: (): ChatbotState => ({
    chatTurns: [],
    isSidebarOpen: false,
    isStreaming: false,
    selectedSessionId: readSelectedSessionId(),
    streamError: '',
    streamingStatus: '',
  }),

  getters: {
    hasChatTurns(state: ChatbotState): boolean {
      return state.chatTurns.length > 0
    },
  },

  actions: {
    appendChatTurn(turn: ChatbotTurn) {
      this.chatTurns.push(turn)
    },
    appendLatestAssistantContent(chunk: string) {
      const latestTurn = this.chatTurns.at(-1)

      if (!latestTurn || latestTurn.role !== 'assistant') {
        return
      }

      latestTurn.content += chunk
    },
    closeSidebar() {
      this.isSidebarOpen = false
    },
    openSidebar() {
      this.isSidebarOpen = true
    },
    resetChatbotStore() {
      this.chatTurns = []
      this.isSidebarOpen = false
      this.isStreaming = false
      this.streamError = ''
      this.streamingStatus = ''
      this.setSelectedSessionId('')
    },
    selectSession(sessionId: string) {
      if (this.selectedSessionId === sessionId) {
        return
      }

      this.setSelectedSessionId(sessionId)
      this.streamError = ''
      this.streamingStatus = ''
    },
    setChatTurns(turns: ChatbotTurn[]) {
      this.chatTurns = turns
    },
    setIsStreaming(value: boolean) {
      this.isStreaming = value
    },
    setSelectedSessionId(sessionId: string) {
      this.selectedSessionId = sessionId

      persistSelectedSessionId(sessionId)
    },
    setStreamError(error: string) {
      this.streamError = error
    },
    setStreamingStatus(status: string) {
      this.streamingStatus = status
    },
    startNewConversation() {
      this.setSelectedSessionId(crypto.randomUUID())
      this.chatTurns = []
      this.streamError = ''
      this.streamingStatus = ''
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useChatbotStore, import.meta.hot))
}
